import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { documents, type DocumentRecord } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/useToast';

import { DeletedDocuments } from './DeletedDocuments';
import { UploadDocumentDialog } from './UploadDocumentDialog';

/**
 * Aba "Documentos da seguradora": a tela /documentos do OnPoint (44, secao 12).
 *
 * Lista o que a seguradora disponibiliza para as corretoras, com Nome, Data de
 * insercao e Acoes. A administracao envia pelo "Upload de documento"; a corretora ve
 * a mesma lista, sem o envio, e baixa os arquivos. Se a lista e de modelos que a
 * corretora baixa ou de exigencias que ela cumpre ainda e pergunta aberta (DOC-001):
 * hoje e a lista de arquivos da seguradora.
 */
export function InsurerDocumentsTab() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [renomeando, setRenomeando] = useState<DocumentRecord | null>(null);
  const [excluindo, setExcluindo] = useState<DocumentRecord | null>(null);

  const podeRenomear = can('documentos.documento.update');
  const podeExcluir = can('documentos.documento.delete');
  // Os excluidos ficam para quem le o repositorio inteiro: a corretora ve so a lista.
  const veExcluidos = can('documentos.documento.view');

  const consulta = useQuery({
    queryKey: ['documents', { ownerType: 'seguradora' }],
    queryFn: ({ signal }) =>
      documents.list({ owner_type: 'seguradora', limit: 100, sort: 'title' }, signal),
  });

  const baixar = useMutation({
    mutationFn: (id: string) => documents.download(id),
    onSuccess: (link) => {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => documents.remove(id),
    onSuccess: () => {
      toast.success('Documento excluido.');
      setExcluindo(null);
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['document-deletions'] });
      void queryClient.invalidateQueries({ queryKey: ['document-storage'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title="Documentos da seguradora"
          description="O que a seguradora disponibiliza para as corretoras. A corretora ve a lista e baixa os arquivos."
          action={<UploadDocumentDialog ownerType="seguradora" group="Seguradora" />}
        />
        {consulta.isError ? (
          <div className="p-5">
            <Alert tone="danger" title="Nao foi possivel carregar os documentos">
              {consulta.error.message}
            </Alert>
          </div>
        ) : (
          <DataTable
            columns={[
              { key: 'nome', header: 'Nome', render: (d) => d.title },
              {
                key: 'insercao',
                header: 'Data de insercao',
                render: (d) => formatDate(d.created_at),
              },
              {
                key: 'acoes',
                header: 'Acoes',
                align: 'right',
                render: (d) => (
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    {d.current_version > 0 ? (
                      <AcaoComIcone
                        acao="baixar"
                        rotulo="Baixar"
                        loading={baixar.isPending && baixar.variables === d.id}
                        onClick={() => baixar.mutate(d.id)}
                      />
                    ) : (
                      <span className="text-2xs text-ink-500">Sem arquivo</span>
                    )}
                    {podeRenomear ? (
                      <AcaoComIcone
                        acao="renomear"
                        rotulo="Renomear"
                        onClick={() => setRenomeando(d)}
                      />
                    ) : null}
                    {podeExcluir ? (
                      <AcaoComIcone
                        acao="excluir"
                        rotulo="Excluir"
                        onClick={() => setExcluindo(d)}
                      />
                    ) : null}
                  </div>
                ),
              },
            ]}
            items={consulta.data?.items ?? []}
            keyOf={(d) => d.id}
            loading={consulta.isPending}
            empty={<EmptyState icon="file" title="Nenhum arquivo foi cadastrado" />}
          />
        )}
      </Card>

      {veExcluidos ? <DeletedDocuments ownerType="seguradora" /> : null}

      {renomeando ? (
        <RenameDialog document={renomeando} onClose={() => setRenomeando(null)} />
      ) : null}

      <Modal
        open={excluindo !== null}
        onClose={() => setExcluindo(null)}
        title="Excluir documento"
        description={
          excluindo
            ? `${excluindo.title}: o documento sai da lista das corretoras e vai para os excluidos.`
            : undefined
        }
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setExcluindo(null)}>
              Voltar
            </Button>
            <Button
              variant="danger"
              loading={excluir.isPending}
              onClick={() => {
                if (excluindo) excluir.mutate(excluindo.id);
              }}
            >
              Excluir documento
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-600">Deseja realmente excluir o documento?</p>
      </Modal>
    </div>
  );
}

/** Renomear (ChangeRequestDocumentName do OnPoint): troca so o nome. */
function RenameDialog({
  document,
  onClose,
}: {
  document: DocumentRecord;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [nome, setNome] = useState(document.title);

  const salvar = useMutation({
    mutationFn: () =>
      documents.update(document.id, {
        group: document.group,
        title: nome,
        required: document.required,
      }),
    onSuccess: () => {
      toast.success('Documento renomeado.');
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Renomear documento"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={salvar.isPending}
            disabled={nome.trim() === ''}
            onClick={() => salvar.mutate()}
          >
            Salvar
          </Button>
        </>
      }
    >
      <Field
        label="Nomear documento"
        required
        value={nome}
        onChange={(event) => setNome(event.target.value)}
      />
    </Modal>
  );
}
